import { gql } from '@apollo/client';

/**
 * Keystone's message-template surface, as the templates harness reads and
 * writes it.
 *
 * Two things shape every operation here. **Reads return the raw body** —
 * placeholders intact, from the lists and from any pinned version — because
 * `renderMessageTemplate` is the only thing that substitutes; that is what
 * lets the editor load a body to edit and a picker preview one before anybody
 * chooses it. And **every write answers with an envelope**
 * (`{ result, warnings, errors }`) rather than failing at the transport level:
 * a body Jinja cannot parse is a successful response describing the problem by
 * path, so the harness renders it against the editor rather than as a banner.
 *
 * The two list queries are deliberately separate rather than one document.
 * `Query.tenant` raises when the token resolves no brokerage — which a
 * Turnstile login does whenever the person holds no membership, or more than
 * one — and a single query would take the personal list down with it.
 */

export type MessageTemplateScope = 'PERSON' | 'PERSON_IN_TENANT' | 'TENANT';

/** One value today. It is on the row and in every query before a second one
 *  needs it, which is the whole reason it exists. */
export type MessageTemplateKind = 'FREE_FORM';

/**
 * Whether an adviser may send a template, which is deliberately not its
 * category: Keystone never behaves differently for a category and always does
 * for a status. `ARCHIVED` is absent until something needs "withdrawn, keep
 * the history" as distinct from a soft delete.
 */
export type MessageTemplateStatus = 'ACTIVE' | 'DRAFT';

export type DiagnosticCode =
  | 'INVALID_TEMPLATE_BODY'
  | 'CONFLICTING_VARIABLE_PATHS'
  | 'MISSING_VARIABLE'
  | 'MISSING_NESTED_VARIABLE'
  | 'UNSTRINGIFIABLE_VARIABLE'
  | 'UNUSED_VARIABLE';

export interface Diagnostic {
  code: DiagnosticCode;
  message: string;
  /** The dotted variable path, or null when the diagnostic is about the body
   *  as a whole — a syntax error names a line, not a variable. */
  path: string | null;
}

export interface TemplateAuthor {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

export interface TemplateVersion {
  id: string;
  versionNumber: number | null;
  title: string | null;
  body: string | null;
  variablePaths: string[] | null;
  /** True when the body is interpolation-only, so every path is genuinely
   *  required rather than merely reachable. */
  pathsAreRequired: boolean | null;
  createdAt: string | null;
  createdBy: TemplateAuthor | null;
}

export interface MessageTemplate {
  id: string;
  category: string | null;
  type: MessageTemplateKind | null;
  status: MessageTemplateStatus | null;
  /** Where the row sits in the list its scope renders. Not unique and not an
   *  identifier: `(position, createdAt, id)` is the order, and the two behind
   *  it are what make it total when several rows share a position. */
  position: number | null;
  personConfigurationId: string | null;
  tenantId: string | null;
  duplicatedFromMessageTemplateId: string | null;
  currentVersionId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  versionCount: number;
  currentVersion: TemplateVersion | null;
}

export interface MessageTemplateWithHistory extends MessageTemplate {
  versions: TemplateVersion[];
}

/** One of the groups a scope's templates are filed under, with how many are
 *  in it. Derived from the templates rather than maintained beside them, so a
 *  group exists exactly as long as something is in it — which is what makes it
 *  the right source for a filter's options. A null `category` is the ungrouped
 *  ones, and it is the same value the `categories` argument takes to fetch
 *  them. */
export interface TemplateCategory {
  category: string | null;
  templateCount: number;
}

export interface TemplateEnvelope<T> {
  result: T | null;
  warnings: Diagnostic[];
  errors: Diagnostic[];
}

const DIAGNOSTIC_FIELDS = `
  code
  message
  path
`;

const VERSION_FIELDS = `
  id
  versionNumber
  title
  body
  variablePaths
  pathsAreRequired
  createdAt
  createdBy {
    id
    firstName
    lastName
  }
`;

const TEMPLATE_FIELDS = `
  id
  category
  type
  status
  position
  personConfigurationId
  tenantId
  duplicatedFromMessageTemplateId
  currentVersionId
  createdAt
  updatedAt
  deletedAt
  versionCount
  currentVersion { ${VERSION_FIELDS} }
`;

/**
 * Both of the caller's own scopes as one list: what travels with them
 * (`PERSON`) plus what they wrote under the token's brokerage
 * (`PERSON_IN_TENANT`). Keystone returns them merged because that is what they
 * are to an adviser; which one a row is in is readable off `tenantId` being
 * null.
 *
 * **`statuses` is named, and it is `MANAGED_STATUSES` rather than an
 * omission.** Omitted means `ACTIVE` alone, which is the composer's reading
 * and the wrong one here twice over: a harness whose drafts were invisible
 * would show nothing wrong when a status write went astray, and a reorder must
 * name every live template in the scope — drafts included — so a list read
 * without them could not produce a permutation Keystone would accept. The
 * status filter on screen narrows this list on the client, like the other
 * three.
 */
export const MY_MESSAGE_TEMPLATES = gql`
  query MyMessageTemplates($statuses: [MessageTemplateStatus!]) {
    me {
      id
      messageTemplates(statuses: $statuses) { ${TEMPLATE_FIELDS} }
      messageTemplateCategories(statuses: $statuses) {
        category
        templateCount
      }
    }
  }
`;

/**
 * What the two lists are read with: everything a management screen manages.
 *
 * Passed to `messageTemplateCategories` as well as to `messageTemplates`, so
 * the counts beside the category options describe the same rows the listing
 * holds. They describe the *unfiltered* listing — a category count does not
 * follow the status filter any more than it follows the scope one, and both
 * are Keystone's enumeration of a whole list rather than of what is on screen.
 */
export const MANAGED_STATUSES: readonly MessageTemplateStatus[] = ['ACTIVE', 'DRAFT'];

export interface MessageTemplateListVariables {
  statuses: readonly MessageTemplateStatus[];
}

export interface MyMessageTemplatesResult {
  me: {
    id: string;
    messageTemplates: MessageTemplate[];
    messageTemplateCategories: TemplateCategory[];
  };
}

/** The brokerage's own list, and the brokerage the token resolved to. */
export const TENANT_MESSAGE_TEMPLATES = gql`
  query TenantMessageTemplates($statuses: [MessageTemplateStatus!]) {
    tenant {
      id
      name
      slug
      messageTemplates(statuses: $statuses) { ${TEMPLATE_FIELDS} }
      messageTemplateCategories(statuses: $statuses) {
        category
        templateCount
      }
    }
  }
`;

export interface TenantMessageTemplatesResult {
  tenant: {
    id: string;
    name: string | null;
    slug: string | null;
    messageTemplates: MessageTemplate[];
    messageTemplateCategories: TemplateCategory[];
  };
}

/**
 * One template with its whole history. `versions` is newest first, which is
 * the order a history is read in.
 */
export const MESSAGE_TEMPLATE_DETAIL = gql`
  query MessageTemplateDetail($id: UUID!) {
    messageTemplate(id: $id) {
      ${TEMPLATE_FIELDS}
      versions { ${VERSION_FIELDS} }
    }
  }
`;

export interface MessageTemplateDetailResult {
  messageTemplate: MessageTemplateWithHistory;
}

/**
 * The one operation that substitutes. A query, not a mutation: it writes
 * nothing, and it is the only field here that will ever be called at volume.
 *
 * An absent bag is an empty one, so rendering a template that requires
 * variables without them produces one `MISSING_VARIABLE` per path rather than
 * an error about the request.
 */
export const RENDER_MESSAGE_TEMPLATE = gql`
  query RenderMessageTemplate($id: UUID!, $versionNumber: Int, $variables: JSON) {
    renderMessageTemplate(id: $id, versionNumber: $versionNumber, variables: $variables) {
      result
      warnings { ${DIAGNOSTIC_FIELDS} }
      errors { ${DIAGNOSTIC_FIELDS} }
    }
  }
`;

export interface RenderMessageTemplateResult {
  renderMessageTemplate: TemplateEnvelope<string>;
}

export interface RenderMessageTemplateVariables {
  id: string;
  versionNumber?: number | null;
  variables?: Record<string, unknown> | null;
}

const PAYLOAD = `
  result {
    ${TEMPLATE_FIELDS}
    versions { ${VERSION_FIELDS} }
  }
  warnings { ${DIAGNOSTIC_FIELDS} }
  errors { ${DIAGNOSTIC_FIELDS} }
`;

/**
 * Every write answers with the same envelope over the same template shape, so
 * one type covers all six — a caller writing one fragment against all of them
 * is the point of there being a single `MessageTemplatePayload` on the schema.
 * Each operation's result is keyed by its own field name.
 */
export type TemplatePayload = TemplateEnvelope<MessageTemplateWithHistory>;

export interface CreateMessageTemplateResult {
  createMessageTemplate: TemplatePayload;
}
export interface EditMessageTemplateResult {
  editMessageTemplate: TemplatePayload;
}
export interface DuplicateMessageTemplateResult {
  duplicateMessageTemplate: TemplatePayload;
}
export interface RevertMessageTemplateResult {
  revertMessageTemplate: TemplatePayload;
}
export interface CategorizeMessageTemplateResult {
  categorizeMessageTemplate: TemplatePayload;
}
export interface SetMessageTemplateStatusResult {
  setMessageTemplateStatus: TemplatePayload;
}
export interface DeleteMessageTemplateResult {
  deleteMessageTemplate: TemplatePayload;
}

export const CREATE_MESSAGE_TEMPLATE = gql`
  mutation CreateMessageTemplate(
    $scope: MessageTemplateScope!
    $type: MessageTemplateType!
    $title: String!
    $body: String!
    $category: String
    $status: MessageTemplateStatus!
  ) {
    createMessageTemplate(
      scope: $scope
      type: $type
      title: $title
      body: $body
      category: $category
      status: $status
    ) { ${PAYLOAD} }
  }
`;

export const EDIT_MESSAGE_TEMPLATE = gql`
  mutation EditMessageTemplate($id: UUID!, $title: String, $body: String) {
    editMessageTemplate(id: $id, title: $title, body: $body) { ${PAYLOAD} }
  }
`;

export const DUPLICATE_MESSAGE_TEMPLATE = gql`
  mutation DuplicateMessageTemplate($id: UUID!, $destination: MessageTemplateScope!) {
    duplicateMessageTemplate(id: $id, destination: $destination) { ${PAYLOAD} }
  }
`;

export const REVERT_MESSAGE_TEMPLATE = gql`
  mutation RevertMessageTemplate($id: UUID!, $versionNumber: Int!) {
    revertMessageTemplate(id: $id, versionNumber: $versionNumber) { ${PAYLOAD} }
  }
`;

export const CATEGORIZE_MESSAGE_TEMPLATE = gql`
  mutation CategorizeMessageTemplate($id: UUID!, $category: String) {
    categorizeMessageTemplate(id: $id, category: $category) { ${PAYLOAD} }
  }
`;

/**
 * Offer a template in the composer, or take it back out.
 *
 * Mints no version, exactly like `categorizeMessageTemplate`: what a template
 * says and whether an adviser may send it are different facts, and a history
 * entry that changed no character of the body would answer "what did this say
 * in June" with a lie. It is not a delete either — a `DRAFT` stays
 * addressable, editable and renderable, which is what previewing unfinished
 * wording needs.
 */
export const SET_MESSAGE_TEMPLATE_STATUS = gql`
  mutation SetMessageTemplateStatus($id: UUID!, $status: MessageTemplateStatus!) {
    setMessageTemplateStatus(id: $id, status: $status) { ${PAYLOAD} }
  }
`;

/**
 * Rewrite one list's order from a full ordered id list.
 *
 * The one operation here that answers with `results` rather than `result`,
 * because it writes every row in the list and naming one of them would be a
 * choice with no defensible answer. `orderedIds` must be an **exact
 * permutation of the scope's live templates**, drafts included and
 * soft-deleted ones excluded; anything partial, padded or repeating is refused
 * as a transport error rather than interpreted, which is what stops a reorder
 * built against a stale list from silently dropping a template or ranking one
 * twice.
 *
 * `scope` names a **list**, not a set of rows: `PERSON` and `PERSON_IN_TENANT`
 * both address the caller's whole personal list, because `me
 * { messageTemplates }` is their union and a reorder that could not move a
 * travelling template past a brokerage-bound one would not be a reorder of
 * anything a client draws.
 *
 * `results` carries the new `position` on each row, so a listing that sorts by
 * it takes the new order out of the cache without a refetch. The versions are
 * deliberately not asked for — a reorder reads no body and mints no version,
 * and every row in the list would carry its whole history back.
 */
export const REORDER_MESSAGE_TEMPLATES = gql`
  mutation ReorderMessageTemplates($scope: MessageTemplateScope!, $orderedIds: [UUID!]!) {
    reorderMessageTemplates(scope: $scope, orderedIds: $orderedIds) {
      results { ${TEMPLATE_FIELDS} }
      warnings { ${DIAGNOSTIC_FIELDS} }
      errors { ${DIAGNOSTIC_FIELDS} }
    }
  }
`;

export interface ReorderMessageTemplatesResult {
  reorderMessageTemplates: {
    results: MessageTemplate[];
    warnings: Diagnostic[];
    errors: Diagnostic[];
  };
}

export interface ReorderMessageTemplatesVariables {
  scope: MessageTemplateScope;
  orderedIds: readonly string[];
}

export const DELETE_MESSAGE_TEMPLATE = gql`
  mutation DeleteMessageTemplate($id: UUID!) {
    deleteMessageTemplate(id: $id) { ${PAYLOAD} }
  }
`;
